import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/system';
import { Grid, Box, Table, TableHead, TableBody, TableRow, TableCell, Typography, IconButton, Button } from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { gridSpacing } from 'utils/constants';
import MainCard from 'components/Cards/MainCard';
import ApiHelper from 'utils/apiHelper';
import AddUserDialog from './AddUserDialog';
import EditUserDialog from './EditUserDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';

const ManagementUsers = () => {
    const theme = useTheme();
    const [usersData, setUsersData] = useState([]);
    const [userGroupsData, setUserGroupsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const [showEditUserDialog, setShowEditUserDialog] = useState(false);
    const [selectedUserData, setSelectedUserData] = useState('');
    const [deleteUserId, setDeleteUserId] = useState();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    const { userInfo } = useSelector((state) => state.auth);

    useEffect(() => {
        getUsersData();
    }, []);

    const getUsersData = () => {
        ApiHelper.get('/api/management/users/')
            .then((res) => {
                setLoading(false);
                setUsersData(res.data.users);
                setUserGroupsData(res.data.user_groups);
            })
            .catch(() => toast.error('Failed to get the users data'));
    };

    const handleCloseAddUserDialog = () => {
        setShowAddUserDialog(false);
        getUsersData();
    };

    const handleCloseEditUserDialog = () => {
        setShowEditUserDialog(false);
        getUsersData();
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeleteUserId(id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleDeleteUser = () => {
        ApiHelper.delete(`/api/management/users/${deleteUserId}/`)
            .then(() => {
                toast.success('User was deleted successfully.');
                getUsersData();
                handleClose();
            })
            .catch(() => {
                toast.error('Failed to delete a user.');
                handleClose();
            });
    };

    const handleEditUserDialog = (data) => {
        setShowEditUserDialog(true);
        setSelectedUserData(data);
    };

    if (!usersData) {
        return null;
    } else {
        return (
            <MainCard title="User Management" cardHeaderShow={false}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={9} md={9}>
                                {/*<Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>*/}
                                {/*    {userInfo.orgid !== null && userInfo.orgid.name}*/}
                                {/*</Typography>*/}
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowAddUserDialog(true)}
                                    startIcon={<Add />}
                                    disabled={userInfo.admin_permissions.user_admin !== true}
                                >
                                    Add User
                                </Button>
                                {showAddUserDialog && <AddUserDialog userGroupsData={userGroupsData} onClose={handleCloseAddUserDialog} />}
                            </Grid>
                        </Grid>
                        {loading === true ? (
                            <Typography sx={{ mt: 3, textAlign: 'center' }}>Please wait...</Typography>
                        ) : usersData.length === 0 ? (
                            <Typography sx={{ mt: 3, textAlign: 'center' }}>No Users Data</Typography>
                        ) : (
                            <Table sx={{ minWidth: 700 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Username</TableCell>
                                        <TableCell>Email Address</TableCell>
                                        <TableCell>SSO User</TableCell>
                                        <TableCell>Validated</TableCell>
                                        <TableCell>Locked</TableCell>
                                        <TableCell>Options</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {usersData.map((data, key) => (
                                        <TableRow key={key}>
                                            <TableCell>
                                                <Typography>{data.username}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{data.email}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{data.sso === true ? 'Yes' : 'No'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{data.probation === 0 ? 'Yes' : 'No'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{data.locked === true ? 'Yes' : 'No'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box>
                                                    <IconButton size="small" onClick={() => handleEditUserDialog(data)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" onClick={() => handleDeleteConfirmation(data.id)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {showEditUserDialog && (
                                        <EditUserDialog
                                            data={selectedUserData}
                                            userGroupsData={userGroupsData}
                                            onClose={handleCloseEditUserDialog}
                                        />
                                    )}
                                    {showDeleteConfirmationDialog && (
                                        <DeleteConfirmationDialog onClose={handleClose} onYes={handleDeleteUser} name="user" />
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementUsers;
